import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { useLocation } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import API from "../services/api";

const rateCards = [
  { key: "single", title: "Single Ank", subtitle: "Single digit bet", color: "#f0fdf4", border: "#86efac", code: "SA" },
  { key: "single_bulk", title: "Single Bulk", subtitle: "Bulk single digit", color: "#ecfeff", border: "#67e8f9", code: "SB" },
  { key: "jodi", title: "Jodi", subtitle: "Two digit pair", color: "#f0fdf4", border: "#86efac", code: "JD" },
  { key: "jodi_bulk", title: "Jodi Bulk", subtitle: "Bulk two digit pairs", color: "#fdf2f8", border: "#f9a8d4", code: "JB" },
  { key: "red_bracket", title: "Red Bracket", subtitle: "Bracket family bet", color: "#fff1f2", border: "#fda4af", code: "RB" },
  { key: "sp", title: "Single Patti", subtitle: "Three digit combination", color: "#fefce8", border: "#fde047", code: "SP" },
  { key: "sp_bulk", title: "SP Bulk", subtitle: "Bulk single patti", color: "#f5f3ff", border: "#fdba74", code: "SBK" },
  { key: "sp_motor", title: "SP Motor", subtitle: "Single patti motor", color: "#faf5ff", border: "#d8b4fe", code: "SM" },
  { key: "dp", title: "Double Patti", subtitle: "Two same digits", color: "#fff7ed", border: "#fdba74", code: "DP" },
  { key: "dp_bulk", title: "DP Bulk", subtitle: "Bulk double patti", color: "#fff7ed", border: "#fb923c", code: "DBK" },
  { key: "dp_motor", title: "DP Motor", subtitle: "Double patti motor", color: "#fef2f2", border: "#fca5a5", code: "DM" },
  { key: "tp", title: "Triple Patti", subtitle: "Three same digits", color: "#fef2f2", border: "#fca5a5", code: "TP" },
  { key: "half_sangam", title: "Half Sangam", subtitle: "Open/Close + Patti", color: "#fff7ed", border: "#fdba74", code: "HS" },
  { key: "full_sangam", title: "Full Sangam", subtitle: "Open + Close Patti", color: "#fdf2f8", border: "#f9a8d4", code: "FS" },
];

const starlineRateCards = [
  { key: "single", title: "Single Ank", subtitle: "Starline single digit", color: "#f0fdf4", border: "#86efac", code: "SA" },
  { key: "sp", title: "Single Patti", subtitle: "Starline single patti", color: "#fefce8", border: "#fde047", code: "SP" },
  { key: "dp", title: "Double Patti", subtitle: "Starline double patti", color: "#fff7ed", border: "#fdba74", code: "DP" },
  { key: "tp", title: "Triple Patti", subtitle: "Starline triple patti", color: "#fef2f2", border: "#fca5a5", code: "TP" },
];

function AdminGameRates() {
  const location = useLocation();
  const isStarline = location.pathname.includes("/starline/");
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const cards = isStarline ? starlineRateCards : rateCards;

  useEffect(() => {
    API.get(`/admin/game-rates?market_type=${isStarline ? "starline" : "normal"}`)
      .then((res) => setForm(res.data || {}))
      .catch(() => setForm({}));
  }, [isStarline]);

  const saveRates = async () => {
    try {
      setSaving(true);
      await API.post(`/admin/game-rates?market_type=${isStarline ? "starline" : "normal"}`, form);
      alert("Game rates saved");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to save game rates");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <section style={page}>
        <div style={header}>
          <div style={{ borderLeft: "4px solid #ea580c", paddingLeft: "14px" }}>
            <h1 style={title}>{isStarline ? "Starline Game Rates" : "Normal Market Rates"}</h1>
            <p style={subtitle}>{isStarline ? "Starline payout multipliers only" : "Game Management rates for all normal market bets"}</p>
            <span style={modeBadge}>{isStarline ? "STARLINE" : "NORMAL MARKET"}</span>
          </div>
          <button type="button" style={saveButton} onClick={saveRates} disabled={saving}>
            <Save size={18} /> {saving ? "Saving..." : "Save Rates"}
          </button>
        </div>

        <div style={grid}>
          {cards.map((card) => (
            <article key={card.key} style={rateCard}>
              <div style={cardHeader}>
                <span style={codeBadge}>{card.code}</span>
                <div style={{ flex: 1 }}>
                  <strong style={cardTitle}>{card.title}</strong>
                  <div style={cardSubtitle}>{card.subtitle}</div>
                </div>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={form[card.key] ?? ""}
                  onChange={(e) => setForm((c) => ({ ...c, [card.key]: e.target.value }))}
                  style={input}
                />
              </div>
            </article>
          ))}
        </div>
      </section>
    </AdminLayout>
  );
}

const page = { display: "grid", gap: "20px" };
const header = { display: "flex", justifyContent: "space-between", alignItems: "flex-start" };
const title = { margin: 0, fontSize: 22, fontWeight: 900, color: "#431407" };
const subtitle = { margin: "6px 0 0", color: "#64748b", fontSize: "13px" };
const modeBadge = { display: "inline-block", marginTop: "10px", padding: "6px 14px", borderRadius: "999px", background: "linear-gradient(135deg, #ffedd5, #fed7aa)", color: "#ea580c", fontSize: "11px", fontWeight: 900, letterSpacing: ".04em" };
const saveButton = { height: "46px", padding: "0 22px", border: 0, background: "linear-gradient(135deg, #ea580c, #7c2d12)", color: "#fff", borderRadius: "12px", display: "flex", alignItems: "center", gap: "8px", fontWeight: 800, cursor: "pointer", boxShadow: "0 4px 14px rgba(79,70,229,0.30)" };
const grid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" };
const rateCard = { background: "#fff", border: "1.5px solid #ffedd5", borderRadius: 16, padding: "18px 20px", borderLeft: "4px solid #ea580c", boxShadow: "0 4px 20px rgba(79,70,229,0.08)", display: "flex", alignItems: "center", gap: "14px" };
const cardHeader = { display: "flex", alignItems: "center", gap: "14px", flex: 1 };
const codeBadge = { minWidth: "46px", height: "46px", display: "grid", placeItems: "center", borderRadius: "12px", background: "linear-gradient(135deg, #ea580c, #7c2d12)", color: "#fff", fontWeight: 900, fontSize: "14px", letterSpacing: ".04em", flexShrink: 0 };
const cardTitle = { display: "block", fontSize: "15px", fontWeight: 800, color: "#431407" };
const cardSubtitle = { marginTop: "3px", color: "#64748b", fontSize: "12px" };
const label = { display: "grid", gap: "10px", color: "#334155", fontWeight: 700 };
const input = { width: "110px", height: "46px", border: "1.5px solid #ffedd5", borderRadius: "12px", padding: "0 14px", fontSize: "16px", fontWeight: 800, background: "#f8faff", color: "#431407", outline: 0, flexShrink: 0 };

export default AdminGameRates;
